import {
  useCustomerService,
  usePersonService,
} from 'modules/customer/services';
import { TQualificationStatusOption } from 'modules/products/pages/interfaces';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, BoxSection } from '../../../app/components';
import { TableArea, TableWrapper } from './styles';
import { Table } from 'app/components';
import { useQueryParam, NumberParam, withDefault } from 'use-query-params';
import { TProducts } from 'modules/products/context';
import {
  useProductsService,
  useQualificationsService,
} from 'modules/products/services/hooks';
import { EmptyContent } from 'modules/products/pages/ProductDetail/components/EmptyContent';
import {
  ConditionsRequiredProperty,
  TPersonCreditLine,
  TQualificationWithStatusAndProduct,
} from 'modules/customer/interfaces/qualifications';
import { PendenciesModal } from 'modules/customer/components/PendenciesModal';
import {
  useUploadBusinessFiles,
  useUploadPersonFiles,
} from 'modules/customer/hooks';
import get from 'lodash.get';
import {
  TRegisterBusinessDataRequest,
  TRegisterPersonDataRequest,
} from 'modules/customer/context';
import { EBeneficiaryType } from 'modules/products/context/ProductsProvider/products.interfaces';
import { TProductVariableConfiguration } from 'modules/products/context/ProductsProvider/products.interfaces';
import {
  renderQualificationTableContent,
  tableQualificationHeader,
} from 'modules/common/QualificationTab/Qualification/Qualification';
import QualificationsSideSheet from './QualificationSideModal/QualificationsSideSheet';
import { EQualificationRole } from 'modules/products/context/QualificationsProvider/qualifications.interfaces';
import { TConditionRequiredProperty } from 'modules/customer/interfaces/qualifications';
import { IFile } from 'modules/customer/hooks/useUploadBusinessFiles';

async function mapPersonCreditLineToQualification(
  line: TPersonCreditLine,
  register: TRegisterPersonDataRequest,
  getProduct: (productId: string) => Promise<{ data?: TProducts } | undefined>,
  getCustomVariables: (
    productId: string,
  ) => Promise<
    | { borrower?: { person?: { name: string; required?: boolean }[] } }
    | undefined
  >,
  userUploadedFiles: IFile[],
  conditionsRequiredProperty: TConditionRequiredProperty[],
  beneficiaryType: typeof EBeneficiaryType,
): Promise<TQualificationWithStatusAndProduct | null> {
  try {
    const productData = await getProduct(line.product_id);
    const productPayload = productData?.data as TProducts | undefined;
    const customVariables = await getCustomVariables(line.product_id);
    const missingDocuments =
      productPayload?.variables_configuration?.borrower?.person_document?.filter(
        (document: { required?: boolean; type: string }) => {
          if (document.required) {
            const inx = userUploadedFiles.findIndex(
              (f) => document.type === f.type,
            );
            return inx === -1;
          }
          return false;
        },
      ) || [];
    const missingVariables: TProductVariableConfiguration[] =
      (customVariables?.borrower?.person?.filter(
        (variable: { required?: boolean; name: string }) => {
          if (variable.required) {
            const variableValue = get(register, variable.name);
            return typeof variableValue === 'undefined';
          }
          return false;
        },
      ) as TProductVariableConfiguration[]) || [];
    const condition = {
      line_of_credit: {
        credit_limit: line.granted_credit,
        consumed_credit_limit: line.consumed_credit,
      },
      schedule_based_conditions: [
        {
          interest_rate: line.monthly_iinterest_rate,
          upper_limit: 0,
        },
      ],
      max_payment_number: null,
      min_payment_number: null,
      tac: null,
      max_grace_period: null,
      min_grace_period: null,
      min_requested_amount: null,
      exp_date: null,
      due_day: null,
    };
    const missingConditions = productPayload?.conditions_required
      ? conditionsRequiredProperty.filter((cond) => {
          const conditionValue = get(condition as object, cond.name, null);
          return conditionValue === null;
        })
      : [];
    const missingBankOrPix =
      (!register.external_bank_account ||
        !register.external_bank_account?.bank_account) &&
      (!register.pix || !register.pix?.key) &&
      productPayload?.beneficiary_type === beneficiaryType.BORROWER;
    const borrower = {
      id: line.borrower_id,
      name: register.full_name ?? '',
      taxpayer_id: register.taxpayer_id,
    };
    const funding = {
      id: line.borrower_id,
      legal_name: register.full_name ?? '',
      taxpayer_id: register.taxpayer_id ?? '',
    };
    return {
      id: line.id,
      borrower,
      borrower_employer: null,
      condition,
      created_at: line.valid_until,
      employer: null,
      funding,
      product: {
        id: line.product_id,
        name: productPayload?.name ?? '',
        description: productPayload?.description ?? '',
      },
      role: EQualificationRole.BORROWER,
      status: line.status,
      updated_at: line.valid_until,
      productData: productPayload ?? ({} as TProducts),
      statusList: [],
      missingDocuments,
      missingVariables,
      missingConditions,
      missingBankOrPix,
      valid_until: line.valid_until,
    };
  } catch {
    return null;
  }
}

interface IQualificationsTabProps {
  register: TRegisterBusinessDataRequest | TRegisterPersonDataRequest;
}
function isPersonRegister(
  register: TRegisterBusinessDataRequest | TRegisterPersonDataRequest,
): register is TRegisterPersonDataRequest {
  return !('legal_name' in register);
}

const QualificationsTab = ({ register }: IQualificationsTabProps) => {
  const isPerson = isPersonRegister(register);

  /* Hooks */
  const params = useParams();
  const { getQualificationStatusOptions, getProductQualificationsLoading } =
    useQualificationsService();
  const { getProduct, getCustomVariables } = useProductsService();
  const { getQualifications: getQualificationsBusiness } = useCustomerService();
  const { getPersonCreditLines } = usePersonService();
  const { uploadedFiles: businessUploadedFilesState, fetchBusinessFile } =
    useUploadBusinessFiles();
  const { uploadedFiles: personUploadedFilesState, fetchPersonFile } =
    useUploadPersonFiles();
  const [linesPerPage, setLinesPerPage] = useQueryParam(
    'size',
    withDefault(NumberParam, 10),
  );
  const [currentPage, setCurrentPage] = useQueryParam(
    'page',
    withDefault(NumberParam, 0),
  );

  /* States */
  const [qualifications, setQualifications] = useState<
    TQualificationWithStatusAndProduct[]
  >([]);
  const [totalItemsQty, setTotalItemsQty] = useState(10);
  const [pendenciesModalOpen, updatePendenciesModalOpen] = useState<
    TQualificationWithStatusAndProduct | undefined
  >(undefined);
  const [finishRequest, setFinishRequest] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState<
    TQualificationWithStatusAndProduct | undefined
  >();

  /* Variables */
  const pid = params?.id as string;
  const isLoading = getProductQualificationsLoading;

  /* Handlers */
  const handleGetBorrowerQualification = async (
    personId: string,
    page = 0,
    itemsPerPage?: number,
  ) => {
    if (isPerson) {
      const userUploadedFiles =
        personUploadedFilesState.length === 0
          ? await fetchPersonFile(personId)
          : personUploadedFilesState;

      const creditLinesResponse = await getPersonCreditLines(
        { borrower_id: personId },
        page,
        itemsPerPage,
      );
      if (creditLinesResponse?.content?.length !== undefined) {
        setTotalItemsQty(creditLinesResponse.total_elements ?? 0);
        const qualificationsList = await Promise.all(
          creditLinesResponse.content.map(async (line: TPersonCreditLine) =>
            mapPersonCreditLineToQualification(
              line,
              register as TRegisterPersonDataRequest,
              getProduct,
              getCustomVariables,
              userUploadedFiles,
              ConditionsRequiredProperty,
              EBeneficiaryType,
            ),
          ),
        );
        setQualifications(
          qualificationsList.filter(
            (item): item is TQualificationWithStatusAndProduct => item !== null,
          ),
        );
      } else {
        setTotalItemsQty(0);
        setQualifications([]);
      }
      setFinishRequest(true);
      return;
    }

    /* Fluxo BUSINESS: useCustomerService.getQualifications com BORROWER e EMPLOYER */
    const businessUploadedFiles =
      businessUploadedFilesState.length === 0
        ? await fetchBusinessFile(personId)
        : businessUploadedFilesState;

    const borrowerContent = await getQualificationsBusiness(
      personId,
      'BORROWER',
      page,
      itemsPerPage,
    );
    const employerContent = await getQualificationsBusiness(
      personId,
      'EMPLOYER',
      page,
      itemsPerPage,
    );
    if (borrowerContent) {
      setTotalItemsQty(
        (borrowerContent?.total_elements || 0) +
          (employerContent?.total_elements || 0),
      );

      const qualificationsList = await Promise.all(
        ([] as Promise<TQualificationWithStatusAndProduct | null>[]).concat(
          borrowerContent.content.map(async (qualification) => {
            try {
              const statusList: TQualificationStatusOption[] =
                await getQualificationStatusOptions(
                  qualification.product.id,
                  'BUSINESS',
                );
              const productData = await getProduct(qualification.product.id);
              const customVariables = await getCustomVariables(
                qualification.product.id,
              );
              const missingDocuments =
                (
                  productData?.data as TProducts
                ).variables_configuration?.borrower.business_document.filter(
                  (document) => {
                    if (document.required) {
                      const inx = businessUploadedFiles.findIndex(
                        (businessUploadedFile) =>
                          document.type === businessUploadedFile.type,
                      );
                      return inx === -1;
                    }
                    return false;
                  },
                ) || [];

              const missingVariables =
                customVariables?.borrower.business.filter((variable) => {
                  if (variable.required) {
                    const variableValue = get(register, variable.name);
                    return typeof variableValue === 'undefined';
                  }
                  return false;
                }) || [];

              const missingConditions = (productData?.data as TProducts)
                .conditions_required
                ? ConditionsRequiredProperty.filter((condition) => {
                    const conditionValue = get(
                      qualification.condition,
                      condition.name,
                      null,
                    );
                    return conditionValue === null;
                  })
                : [];

              const missingBankOrPix =
                (!register.external_bank_account ||
                  !register.external_bank_account?.bank_account) &&
                (!register.pix || !register.pix?.key) &&
                (productData?.data as TProducts)?.beneficiary_type ===
                  EBeneficiaryType.BORROWER;

              return {
                ...qualification,
                statusList,
                missingDocuments,
                missingConditions,
                missingVariables,
                missingBankOrPix,
                productData: productData?.data as TProducts,
              };
            } catch {
              return null;
            }
          }),
          employerContent
            ? employerContent.content.map(async (qualification) => {
                try {
                  const statusList: TQualificationStatusOption[] =
                    await getQualificationStatusOptions(
                      qualification.product.id,
                      'BUSINESS',
                    );
                  const productData = await getProduct(
                    qualification.product.id,
                  );
                  const customVariables = await getCustomVariables(
                    qualification.product.id,
                  );

                  const missingDocuments =
                    (
                      productData?.data as TProducts
                    ).variables_configuration?.employer_document.filter(
                      (document) => {
                        if (document.required) {
                          const inx = businessUploadedFiles.findIndex(
                            (businessUploadedFile) =>
                              document.type === businessUploadedFile.type,
                          );
                          return inx === -1;
                        }
                        return false;
                      },
                    ) || [];

                  const missingVariables =
                    customVariables?.employer.filter((variable) => {
                      if (variable.required) {
                        const variableValue = get(register, variable.name);
                        return typeof variableValue === 'undefined';
                      }
                      return false;
                    }) || [];

                  const missingBankOrPix =
                    (!register.external_bank_account ||
                      !register.external_bank_account?.bank_account) &&
                    (!register.pix || !register.pix?.key);

                  return {
                    ...qualification,
                    statusList,
                    missingDocuments,
                    missingConditions: [],
                    missingVariables,
                    missingBankOrPix,
                    productData: productData?.data as TProducts,
                  };
                } catch {
                  return null;
                }
              })
            : [],
        ),
      );
      setQualifications(
        qualificationsList.filter(
          (item) => item !== null,
        ) as TQualificationWithStatusAndProduct[],
      );
    } else {
      setTotalItemsQty(0);
      setQualifications([]);
    }
    setFinishRequest(true);
  };

  useEffect(() => {
    setFinishRequest(false);
    handleGetBorrowerQualification(pid, currentPage, linesPerPage);
  }, [currentPage, linesPerPage]);

  return (
    <BoxSection level="wrapper">
      {isLoading || !finishRequest ? (
        <div
          style={{
            paddingTop: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignSelf: 'center',
            width: '100%',
          }}
        >
          <Spinner />
        </div>
      ) : qualifications?.length <= 0 && finishRequest ? (
        <EmptyContent titleText="Nenhuma linha de crédito encontrada" />
      ) : (
        qualifications.length > 0 &&
        finishRequest && (
          <TableArea style={{ marginTop: '40px' }}>
            <TableWrapper>
              <Table
                showPagination
                overflowVisible
                handlePageChange={(selectedPage) => {
                  setCurrentPage(selectedPage - 1);
                }}
                handleLinesPerPage={(selectedLinesPerPage) => {
                  setCurrentPage(0);
                  setLinesPerPage(
                    selectedLinesPerPage === 0 ? 10 : selectedLinesPerPage,
                  );
                }}
                totalElementsQty={totalItemsQty}
                currentPage={currentPage + 1}
                linesPerPage={linesPerPage}
                tableHeadCell={tableQualificationHeader}
                tableBodyContent={qualifications?.map((qualification) => {
                  return {
                    id: qualification?.id,
                    cells: renderQualificationTableContent(
                      qualification as TQualificationWithStatusAndProduct,
                      setSelectedQualification,
                    ),
                  };
                })}
              />
            </TableWrapper>
          </TableArea>
        )
      )}
      <PendenciesModal
        isOpen={pendenciesModalOpen !== undefined}
        qualification={pendenciesModalOpen}
        handleClose={() => updatePendenciesModalOpen(undefined)}
      />
      <QualificationsSideSheet
        isOpen={!!selectedQualification}
        handleClose={() => setSelectedQualification(undefined)}
        qualification={selectedQualification}
        productInfo={selectedQualification?.productData}
        borrowerQualificationData={selectedQualification}
      />
    </BoxSection>
  );
};

export default QualificationsTab;
